import { FormInstance } from "antd";
import { QueryOperation, SearchQuery } from "../code/search-query/search-query";
import {
  buildSearchQuery,
  comboboxValue,
  dateValue,
  QueryFieldMapping,
} from "../code/search-query/query-mapper";
import { DataColumn } from "../controls/Data.default";
import { DynamicSchema } from "../controls/DynamicForm.default";
import { InputObject } from "../controls/default";
import {
  createScopedFilterRequest,
  createSearchRequest,
} from "../hooks/useSearchRequest";
import {
  getCompanies,
  getCompanyAssets,
  getCompanyDepartments,
  getCompanyUserRoles,
  getCountries,
  getLanguages,
} from "../repository/data.repository";
import { GenderType } from "../repository/default";

export interface EmployeesViewForm {
  readonly userName: string;
  readonly firstName: string;
  readonly middleName: string;
  readonly lastName: string;
  readonly dateOfBirth: string;
  readonly email: string;
  readonly phoneNumber: string;
  readonly company: InputObject;
  readonly department?: InputObject;
  readonly role?: InputObject;
  readonly asset?: InputObject;
  readonly gender: string;
  readonly testLanguage: InputObject;
  readonly country: InputObject;
  readonly internalId: string;
  readonly externalId: string;
}

export const employeesViewColumns: DataColumn[] = [
  {
    title: "Company",
    key: "companyName",
    width: 192,
    sort: true,
    defaultSort: "asc",
    fixed: "left",
  },
  // §4.5's "work structure" group is Company, department, vessel/site and role/job position. Only Company
  // was a column; the other three were on the wire and populated — department and role on all 92 seeded
  // employees, asset on 89 — and simply never shown. This is the group PD-01 leans on hardest for
  // employees, because it is what tells a reader WHERE a person works rather than who they are.
  {
    title: "Department",
    key: "departmentName",
    width: 192,
    sort: true,
  },
  {
    title: "Role",
    key: "roleName",
    width: 160,
    sort: true,
  },
  // "Asset", not "Vessel/Site" — see the note on the All grid. This is the field crew rotation turns on.
  {
    title: "Asset",
    key: "assetName",
    width: 192,
    sort: true,
  },
  {
    title: "User Name",
    key: "userName",
    width: 192,
    sort: true,
  },
  {
    title: "First Name",
    key: "firstName",
    width: 192,
    sort: true,
  },
  {
    title: "Middle Name",
    key: "middleName",
    width: 192,
    sort: true,
  },
  {
    title: "Last Name",
    key: "lastName",
    width: 192,
    sort: true,
  },
  {
    title: "Date Of Birth",
    key: "dateOfBirth",
    width: 128,
    format: "DD/MM/YYYY",
    sort: true,
  },
  {
    title: "Email",
    key: "email",
    width: 256,
    sort: true,
  },
  // Employees was the ONLY tab without this: All and Managers have shown it all along. §4.5 asks for
  // "activation status" in the employee status group, and email confirmation is what the product means
  // by it — the same flag those two tabs already display.
  {
    title: "Email Confirmed",
    key: "emailConfirmed",
    width: 128,
    sort: true,
  },
  {
    title: "Phone",
    key: "phoneNumber",
    width: 192,
    sort: true,
  },
  {
    title: "Gender",
    key: "gender",
    width: 192,
    sort: true,
  },
  {
    title: "Test Language",
    key: "testLanguageName",
    width: 192,
    sort: true,
  },
  {
    title: "Country",
    key: "countryName",
    width: 192,
    sort: true,
  },
  {
    title: "Internal Ref Id",
    key: "internalId",
    width: 192,
    sort: true,
  },
  {
    title: "External Ref Id",
    key: "externalId",
    width: 192,
    sort: true,
  },
  // §4.5's status/activity group opens here. `joinedDate` rather than `createdOn`: both are on the wire
  // and in the seed they differ by a second, but they answer different questions — createdOn is when the
  // ACCOUNT was made, joinedDate is when the person joined the company, and for an employee record it is
  // the second that "registration" means. Last login and status will join this column when they exist;
  // they are deliberately last so the group grows together rather than scattering across the row.
  {
    title: "Registration Date",
    key: "joinedDate",
    width: 144,
    format: "DD/MM/YYYY",
    sort: true,
  },
  // §4.3 lists "last login" among the All-grid fields. A real column rather than a derived
  // MAX(LoginHistory.Timestamp), so sorting on it stays cheap. Blank until the person signs in again:
  // the field was renamed from a dead LastOnline and deliberately NOT back-filled, because 902 of the
  // 2 086 seeded login-history rows are dated in the FUTURE and a back-fill would import that.
  {
    title: "Last Login",
    key: "lastLoginAt",
    width: 144,
    format: "DD/MM/YYYY HH:mm",
    sort: true,
  },
  // Admin requirement 2026-11-03.
  {
    title: "Status",
    key: "isActive",
    width: 110,
    sort: true,
    transform: (v: boolean) => (v === false ? "Inactive" : "Active"),
  },
  {
    title: "Activated On",
    key: "activatedAt",
    width: 148,
    format: "DD/MM/YYYY",
    sort: true,
  },
  {
    title: "Deactivated On",
    key: "deactivatedAt",
    width: 148,
    format: "DD/MM/YYYY",
    sort: true,
  },
];

const employeesQueryMappings: QueryFieldMapping[] = [
  { formField: "company", queryField: "companyUid", operation: QueryOperation.Equal, transform: comboboxValue },
  // §4.5's work-structure group, now filterable as well as visible. Asset is the one an operator reaches
  // for most here — "who is currently on this vessel" is the crew question the tab exists to answer.
  { formField: "department", queryField: "departmentUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "role", queryField: "roleUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "asset", queryField: "assetUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "userName" },
  { formField: "firstName" },
  { formField: "middleName" },
  { formField: "lastName" },
  { formField: "dateOfBirth", operation: QueryOperation.Equal, transform: dateValue },
  { formField: "email" },
  { formField: "phoneNumber" },
  { formField: "gender", queryField: "gender", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "testLanguage", queryField: "testLanguageUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "country", queryField: "countryUid", operation: QueryOperation.Equal, transform: comboboxValue },
  // Internal Id is numeric (long?) → exact Equal. External Id is a free-text ref → StartsWith
  // (the mapper default) for partial lookups, matching every other text filter on this grid.
  { formField: "internalId", queryField: "internalId", operation: QueryOperation.Equal },
  { formField: "externalId", queryField: "externalId" },
];

export const mapEmployeesViewQuery = (values: EmployeesViewForm): SearchQuery =>
  buildSearchQuery(values, employeesQueryMappings);

const gendersRequest = () =>
  Promise.resolve(
    Object.entries(GenderType).map(
      ([value, display]) =>
        ({
          display,
          value,
        }) as InputObject,
    ),
  );

// A factory rather than a constant — Department / Role / Asset scope their options to the chosen Company.
// See the note on the All tab: `dependsOn` is a cascade reset, not a visibility rule.
export const employeesSearchSchema = (form: FormInstance<any>): DynamicSchema => ({
  groups: [
    {
      controls: [
        {
          type: "combobox",
          name: "company",
          label: "Company",
          request: createSearchRequest(getCompanies, "name"),
        },
        {
          type: "combobox",
          name: "department",
          label: "Department",
          dependsOn: ["company"],
          request: createScopedFilterRequest(getCompanyDepartments, "name", () => [
            ["companyUid", form.getFieldValue("company")?.value],
          ], undefined, { subDisplayField: "companyName" }),
        },
        {
          type: "combobox",
          name: "role",
          label: "Role",
          dependsOn: ["company"],
          request: createScopedFilterRequest(getCompanyUserRoles, "name", () => [
            ["companyUid", form.getFieldValue("company")?.value],
          ], undefined, { subDisplayField: "companyName" }),
        },
        {
          type: "combobox",
          name: "asset",
          label: "Asset",
          dependsOn: ["company"],
          request: createScopedFilterRequest(getCompanyAssets, "name", () => [
            ["companyUid", form.getFieldValue("company")?.value],
          ], undefined, { subDisplayField: "companyName" }),
        },
        { type: "textbox", name: "userName", label: "User Name" },
        { type: "textbox", name: "firstName", label: "First Name" },
        { type: "textbox", name: "middleName", label: "Middle Name" },
        { type: "textbox", name: "lastName", label: "Last Name" },
        { type: "datepicker", name: "dateOfBirth", label: "Date Of Birth" },
        { type: "textbox", name: "email", label: "Email" },
        { type: "textbox", name: "phoneNumber", label: "Phone" },
        {
          type: "combobox",
          name: "gender",
          label: "Gender",
          request: gendersRequest,
        },
        {
          type: "combobox",
          name: "testLanguage",
          label: "Test Language",
          request: createSearchRequest(getLanguages, "name"),
        },
        {
          type: "combobox",
          name: "country",
          label: "Country",
          request: createSearchRequest(getCountries, "name"),
        },
        { type: "textbox", name: "internalId", label: "Internal Id" },
        { type: "textbox", name: "externalId", label: "External Id" },
      ],
    },
  ],
});
